import { IConversionHour } from 'store/modules/report/types'

// Texto da caixa "Padrão horário" do gráfico de conversão.
//
// "Manhã fraca, tarde forte" vale para praticamente toda loja, então sozinho
// não diz nada. O que diferencia uma loja da outra é a hora em que o fluxo
// passa da média (11h–12h em loja de rua, 14h–15h em shopping) e onde fica o
// pico. A frase parte disso e acrescenta, no máximo, uma exceção:
//
// 1. pico ao meio-dia (substitui a frase base);
// 2. pico isolado na manhã, antes de o fluxo firmar acima da média;
// 3. fechamento fraco: horas finais abaixo da média, a partir de quando.
//
// Antes disso, confere se existe mesmo diferença entre manhã e tarde. Em
// período curto ou loja de fluxo espalhado, várias horas soltas ficam acima da
// média e a "virada" vira ruído — nesse caso a frase diz que o fluxo está
// distribuído (ou que a manhã é mais forte) em vez de inventar um padrão.
//
// "Média" é a mesma linha do gráfico: média das faixas horárias exibidas.
//
// Atenção ao que `visitors` significa: é a SOMA daquela faixa horária em todos
// os dias do período filtrado, e não quantas pessoas entram numa hora de loja
// aberta. Num período de 24 dias os dois diferem por 24x.

const MORNING_UNTIL = 11
const NOON = 12
const AFTERNOON_FROM = 13

// Quanto a média por hora da tarde precisa superar a da manhã para contar como
// "tarde forte". Nas 39 lojas do período de um mês a menor razão foi 1,32
// (Icaraí); numa semana da Asa Sul com fluxo espalhado, 0,98.
const PERIOD_DIFFERENCE = 1.2

// Abaixo disso, a variação aleatória da contagem é do tamanho da diferença que
// a frase tenta descrever. Nas 39 lojas analisadas (setembro/2026), as menores têm
// ~25 pessoas/dia: chegam lá em cerca de 10 dias; shoppings, em 2 a 3.
const MIN_VISITORS = 250

const averageOf = (hours: IConversionHour[]) =>
  hours.reduce((acc, hour) => acc + hour.visitors, 0) / hours.length

// Frase principal quando a tarde é claramente mais forte que a manhã.
const describeAfternoonPattern = (
  hours: IConversionHour[],
  isAbove: (hour?: IConversionHour) => boolean,
  peak: IConversionHour
) => {
  if (peak.hour === NOON) {
    return 'Pico ao meio-dia, com a manhã abaixo da média'
  }

  // A virada é a primeira hora que fica acima da média por duas horas
  // seguidas. Exigir duas evita que um pico isolado de manhã conte como o
  // momento em que a loja "esquenta".
  const turnIndex = hours.findIndex(
    (hour, index) => isAbove(hour) && isAbove(hours[index + 1])
  )

  if (turnIndex === -1) return `Pico às ${peak.hour}h`

  const turn = hours[turnIndex]
  const peakPart =
    turn.hour === peak.hour
      ? 'que é o horário de pico'
      : `com pico às ${peak.hour}h`

  const morningSpikes = hours
    .slice(0, turnIndex)
    .filter(hour => hour.hour <= MORNING_UNTIL && isAbove(hour))

  if (morningSpikes.length) {
    const spikes = morningSpikes.map(hour => `${hour.hour}h`).join(' e ')

    return `Manhã abaixo da média, exceto às ${spikes}. O fluxo firma acima da média a partir das ${turn.hour}h, ${peakPart}`
  }

  if (turn.hour <= MORNING_UNTIL) {
    return `O fluxo passa da média já às ${turn.hour}h, ${peakPart}`
  }

  return `Manhã abaixo da média. O fluxo passa da média a partir das ${turn.hour}h, ${peakPart}`
}

const getConversionInsight = (hours: IConversionHour[]): string => {
  if (!hours.length) return ''

  const total = hours.reduce((acc, hour) => acc + hour.visitors, 0)

  if (total < MIN_VISITORS) {
    return `Fluxo baixo no período (${total.toLocaleString(
      'pt-BR'
    )} pessoas) para identificar um padrão. Amplie o filtro de datas.`
  }

  const average = averageOf(hours)
  const isAbove = (hour?: IConversionHour) => !!hour && hour.visitors >= average

  const peak = hours.reduce((acc, hour) =>
    hour.visitors > acc.visitors ? hour : acc
  )

  const morning = hours.filter(hour => hour.hour <= MORNING_UNTIL)
  const afternoon = hours.filter(hour => hour.hour >= AFTERNOON_FROM)

  // Sem manhã ou sem tarde (loja que só abre num dos turnos) não há o que
  // comparar: a razão assume o limite para cair direto na regra da virada.
  const ratio =
    morning.length && afternoon.length && averageOf(morning) > 0
      ? averageOf(afternoon) / averageOf(morning)
      : PERIOD_DIFFERENCE

  // Com a manhã mais forte, a tarde inteira já tende a ficar abaixo da média:
  // falar do fechamento seria repetir a mesma coisa.
  if (ratio < 1 / PERIOD_DIFFERENCE) {
    return `Manhã mais forte que a tarde, com pico às ${peak.hour}h.`
  }

  // Fluxo distribuído: sem turno forte, "pico" e "fechamento" são ruído (várias
  // horas soltas ficam abaixo da média no meio do dia). O que vale dizer é a
  // faixa em que a loja fica movimentada — da primeira à última hora acima da
  // média — o que já deixa as pontas fracas de fora.
  if (ratio < PERIOD_DIFFERENCE) {
    const first = hours.find(hour => isAbove(hour)) as IConversionHour
    const last = [...hours]
      .reverse()
      .find(hour => isAbove(hour)) as IConversionHour

    return `Fluxo distribuído entre ${first.hour}h e ${last.hour}h, sem um turno mais forte.`
  }

  const sentences = [describeAfternoonPattern(hours, isAbove, peak)]

  // Início do trecho final abaixo da média, contando de trás para frente.
  // Sempre para em alguma hora, porque ao menos o pico está acima da média.
  let closingIndex = hours.length
  while (!isAbove(hours[closingIndex - 1])) closingIndex--

  const closingHours = hours.length - closingIndex

  if (closingHours > 1) {
    sentences.push(
      `A partir das ${hours[closingIndex].hour}h o fluxo fica abaixo da média`
    )
  } else if (closingHours === 1) {
    sentences.push('A última hora fica abaixo da média')
  }

  return `${sentences.join('. ')}.`
}

export default getConversionInsight
